import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Avatar, MobileStepper } from '@mui/material';
import {
  ArrowBackIosNew, ArrowForwardIos, Close,
} from '@mui/icons-material';
import SwipeableViews from 'react-swipeable-views';
import { ProfileUser } from 'lib/types/users';
import { ProfileLink } from 'components/ProfileLink';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { Post } from 'lib/types';
import { useModal } from 'mui-modal-provider';
import IosShare from '@mui/icons-material/IosShare';
import { ShareModal } from 'components/ShareModal';
import {
  CardUrls, Horoscope, UserSnapshot, TopLocationRole,
} from 'lib/types/replay';
import { isEmpty } from 'lodash';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useViewportAspectRatio } from 'lib/hooks/useViewportAspectRatio';
import { AlertModal } from 'components/AlertModal';
import { Slide01 } from './Slide01/Animation';
import { Slide02 } from './Slide02/Animation';
import { Slide03 } from './Slide03/Animation';
import { Slide04 } from './Slide04/Animation';
import { Slide05 } from './Slide05/Animation';
import { Slide06 } from './Slide06/Animation';
import { Slide07 } from './Slide07/Animation';
import { Slide08 } from './Slide08/Animation';
import { Slide09 } from './Slide09/Animation';
import { AnimatedBackground } from './AnimatedBackground';

export const ALL_SLIDES = ['01', '02', '03', '04', '13', '05', '06', '07', '08', '09'];

interface Props {
  profileUser: ProfileUser;
  isFromGondolaApp: boolean;
  overallStats: any;
  topPosts: Post[];
  procrastinationScore: number | null;
  topRoles: any;
  topLocationRole: TopLocationRole;
  allTimeStats: any;
  horoscope: Horoscope;
  startOfYearStats: UserSnapshot;
  cardUrls: CardUrls;
}

export const Replay2025 = ({
  profileUser,
  isFromGondolaApp,
  overallStats,
  topPosts,
  procrastinationScore,
  topRoles,
  topLocationRole,
  allTimeStats,
  horoscope,
  startOfYearStats,
  cardUrls,
}: Props) => {
  const router = useRouter();
  const { showModal } = useModal();

  const is916 = useViewportAspectRatio();

  const [startSlides, setStartSlides] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [slides, setSlides] = useState<string[]>([]);
  const [steps, setSteps] = useState(1);
  const textColor = 'text-white';
  const arrowColors = 'text-gray-300 disabled:text-gray-700';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const stepperStyle = !isFromGondolaApp ? {
    backgroundColor: '#202027',
    '& .MuiMobileStepper-dot': {
      backgroundColor: '#cecfd3',
    },
    '& .MuiMobileStepper-dotActive': {
      backgroundColor: '#F76F4A',
    },

  } : {
    backgroundColor: '#000',
    '& .MuiMobileStepper-dot': {
      backgroundColor: '#cecfd3',
    },
    '& .MuiMobileStepper-dotActive': {
      backgroundColor: '#F76F4A',
    },
  };

  const PERCENTILE_THRESHOLD = 40;
  const canShowSlide = (slide: string) => {
    if (slide === '00') {
      return true;
    }
    if (slide === '01') {
      return !!profileUser.username && !!overallStats?.posts;
    } if (slide === '02') {
      return !!topLocationRole.type
        && (topLocationRole.percentile || 0) * 100 <= PERCENTILE_THRESHOLD;
    } if (slide === '03') {
      return topRoles?.length >= 1;
    } if (slide === '04') {
      return !!topPosts?.[0];
    } if (slide === '05') {
      return !!startOfYearStats?.posts && !!allTimeStats?.posts;
    } if (slide === '06') {
      return !!allTimeStats?.posts;
    } if (slide === '07') {
      return !!horoscope && !!horoscope.imageUrl;
    } if (slide === '08') {
      return !!procrastinationScore;
    } if (slide === '09') {
      return !overallStats?.posts;
    }
    return false;
  };

  useEffect(() => {
    if (window.innerHeight < 600) {
      const modal = showModal(AlertModal, {
        text: 'Please use a device with a larger screen height (or rotate your device) for the best experience.',
        onClose: () => {
          modal.destroy();
          router.reload();
        },
      });
    } else {
      setStartSlides(true);
    }
  }, [showModal]);

  useEffect(() => {
    const getSlidesForUser = () => {
      const filteredSlides = ALL_SLIDES.filter((slide) => canShowSlide(slide));
      setSlides(filteredSlides);
      setSteps((isMobile || filteredSlides.length < 2 || isEmpty(cardUrls))
        ? filteredSlides.length : filteredSlides.length + 1);
    };

    getSlidesForUser();
  }, [profileUser, isMobile, cardUrls]);

  const next = () => {
    if (activeStep >= steps - 1) return;

    // If moving to the last step (share modal step), trigger the modal
    if (activeStep === steps - 2 && slides.length > 1 && !isEmpty(cardUrls)) {
      const modal = showModal(ShareModal, {
        onClose: () => {
          modal.destroy();
        },
        text: '#GondolaReplay',
        cardUrls,
        currentCard: slides[0],
      });
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const back = () => {
    if (activeStep <= 0) return;
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const changeStep = (step: number) => {
    // If trying to go to the virtual share step, trigger share modal with first card
    if (step >= slides.length) {
      const modal = showModal(ShareModal, {
        onClose: () => {
          modal.destroy();
        },
        text: '#GondolaReplay',
        cardUrls,
        currentCard: slides[0],
      });
      return;
    }
    setActiveStep(step);
  };

  const getContent = (slideKey: string) => {
    if (!canShowSlide(slideKey)) {
      return undefined;
    }
    switch (slideKey) {
    case '01':
      return (
        <Slide01
          overallStats={overallStats}
          username={profileUser.username}
        />
      );
    case '02':
      return (
        <Slide02
          topLocationRole={topLocationRole}
        />
      );
    case '03':
      return (
        <Slide03
          topRoles={topRoles}
        />
      );
    case '04':
      return (
        <Slide04
          topPost={topPosts[0]}
        />
      );
    case '05':
      return (
        <Slide05
          yearStart={startOfYearStats}
          yearEnd={allTimeStats}
        />
      );
    case '06':
      return (
        <Slide06
          allTimeStats={allTimeStats}
        />
      );
    case '07':
      return (
        <Slide07
          horoscope={horoscope}
        />
      );
    case '08':
      return (
        <Slide08
          procrastinationScore={procrastinationScore || 0}
        />
      );
    case '09':
      return (
        <Slide09 />
      );
    default:
      return <div />;
    }
  };

  const handleModal = () => {
    const modal = showModal(ShareModal, {
      onClose: () => {
        modal.destroy();
      },
      text: '#GondolaReplay',
      cardUrls,
      currentCard: slides[activeStep],
    });
  };

  if (!startSlides) {
    return <div className="max-w-screen-xl m-auto h-screen relative" />;
  }

  return (
    <div className={textColor}>
      <div className="max-w-screen-xl m-auto h-screen relative">
        <div className="hidden md:block pt-6 absolute top-2 left-2">
          <div className="flex gap-2 items-center">
            <ProfileLink
              user={profileUser}
              context="replay"
            >
              <Avatar
                variant="circular"
                alt={profileUser.name}
                src={imageAvatarUrl(profileUser.avatarUrl)}
                imgProps={{ loading: 'lazy' }}
                className="hover:border-2 border-orange"
              />
            </ProfileLink>
            <h2 className="font-semibold text-xl">
              <ProfileLink
                user={profileUser}
                context="replay"
              >
                {profileUser.name}
              </ProfileLink>
            </h2>
          </div>
        </div>
        <div className="h-screen max-w-md m-auto relative">
          <div className="absolute -left-12 top-1/2 -translate-y-1/2 z-10 hidden sm:block">
            <button
              type="button"
              disabled={activeStep === 0}
              className={arrowColors}
              onClick={back}
            >
              <ArrowBackIosNew fontSize="large" />
            </button>
          </div>
          <div className="absolute -right-12 top-1/2 -translate-y-1/2 z-10 hidden sm:block">
            <button
              type="button"
              disabled={slides.length < 2}
              className={arrowColors}
              onClick={activeStep === slides.length - 1 ? () => {
                const modal = showModal(ShareModal, {
                  onClose: () => {
                    modal.destroy();
                  },
                  text: '#GondolaReplay',
                  cardUrls,
                  currentCard: slides[0],
                });
              } : next}
            >
              <ArrowForwardIos fontSize="large" />
            </button>
          </div>
          <div className="flex justify-center md:items-center min-h-[100svh] max-h-[100svh]">
            <div className="absolute top-0 w-full z-10">
              <MobileStepper
                steps={steps}
                position="static"
                activeStep={activeStep}
                sx={stepperStyle}
                backButton={<div />}
                nextButton={<div />}
              />
            </div>

            <div className={`overflow-hidden w-full ${is916 ? 'h-screen' : 'max-w-[calc(9/16*100svh)] max-h-screen'}`}>
              <SwipeableViews
                index={activeStep}
                onChangeIndex={changeStep}
                onSwitching={(index, type) => {
                  // Detect when user is trying to swipe forward from the last slide
                  if (type === 'move' && index > slides.length - 1 && activeStep === slides.length - 1) {
                    const modal = showModal(ShareModal, {
                      onClose: () => {
                        modal.destroy();
                      },
                      text: '#GondolaReplay',
                      cardUrls,
                      currentCard: slides[0],
                    });
                  }
                }}
                enableMouseEvents
              >
                {slides.map((s, i) => (
                  <AnimatedBackground
                    key={s}
                    slideKey={s}
                    active={activeStep === i}
                    content={() => getContent(s)}
                  />
                ))}
              </SwipeableViews>
            </div>
          </div>
        </div>
        <div className="hidden md:block pt-6 font-semibold text-xl absolute top-2 right-2">
          #GondolaReplay
        </div>
        <div className="absolute top-8 md:top-16 right-4 z-10">
          <div className="flex md:flex-col gap-2 items-stretch w-full">
            {(!!cardUrls && !isEmpty(cardUrls)) && (
              <button
                type="button"
                className="flex items-center gap-2 rounded-full bg-white opacity-60 hover:opacity-100 text-black font-semibold border p-1 md:px-4"
                onClick={handleModal}
              >
                <IosShare color="action" />
                <span className="hidden md:block">{!navigator.share ? 'Copy' : 'Share'}</span>
              </button>
            )}
            <div>
              <button
                type="button"
                aria-label="close"
                onClick={() => router.push('/')}
                className="md:hidden rounded-full p-1 bg-white opacity-60 hover:opacity-100 border"
              >
                <Close color="action" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
